/**
 * DailyMatchesFeed — buyer-side surface for the $19.99 Daily Matches product.
 *
 * - Owner path: renders every feed row we've generated (newest first), each
 *   showing 3 AI-picked RVs. Views + clicks are auto-tracked so the advisor
 *   who owns the buyer's lead sees engagement.
 * - Non-owner path: paywall card with a CTA to /checkout/daily_matches.
 *
 * Ownership is inferred from /api/my/purchases (already fetched upstream)
 * to avoid an extra roundtrip.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import {
  Bell, Sparkles, Loader2, ExternalLink, Search, Lock,
  TrendingUp, ChevronRight, DollarSign, Users, Ruler,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DailyMatchesFeed = ({ email }) => {
  const [status, setStatus]   = useState(null);   // {owned, days_active, todays_ready}
  const [feeds, setFeeds]     = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  const target = (email || '').trim().toLowerCase();

  useEffect(() => {
    if (!target) { setLoading(false); return; }
    let cancelled = false;
    (async () => {
      try {
        const s = await axios.get(`${API}/daily-matches/status`, { params: { email: target } });
        if (cancelled) return;
        setStatus(s.data);
        if (!s.data?.owned) { setLoading(false); return; }
        setGenerating(!s.data.todays_ready);
        // Feed call lazily generates today's picks (may take ~15s on first call).
        const f = await axios.get(`${API}/daily-matches/feed`, { params: { email: target, limit: 21 } });
        if (cancelled) return;
        setFeeds(f.data?.feeds || []);
        setGenerating(false);
      } catch {
        if (!cancelled) { setStatus({ owned: false }); }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [target]);

  if (loading) {
    return (
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-8 flex items-center justify-center" data-testid="daily-matches-loading">
        <Loader2 className="w-5 h-5 animate-spin text-cyan-300" />
      </div>
    );
  }

  if (!status?.owned) {
    return <DailyMatchesPaywall />;
  }

  return (
    <div className="space-y-4" data-testid="daily-matches-feed">
      <ActiveBanner daysActive={status.days_active} totalRvs={feeds.reduce((n, f) => n + (f.rvs?.length || 0), 0)} />
      {generating && (
        <div className="rounded-2xl border border-cyan-400/30 bg-cyan-500/10 p-4 flex items-center gap-3">
          <Loader2 className="w-4 h-4 animate-spin text-cyan-300" />
          <p className="text-sm text-slate-200">Crafting today&apos;s 3 fresh picks with our AI matcher… this takes about 15 seconds the first time.</p>
        </div>
      )}
      {feeds.length === 0 && !generating && (
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-6 text-center text-sm text-slate-400">
          Your first delivery lands the moment we finish crafting your picks. Refresh in a few seconds.
        </div>
      )}
      {feeds.map((f) => <FeedDay key={f.id} feed={f} email={target} />)}
    </div>
  );
};

const ActiveBanner = ({ daysActive, totalRvs }) => (
  <div className="relative overflow-hidden rounded-2xl border border-cyan-400/30 bg-gradient-to-br from-cyan-500/10 via-sky-500/8 to-transparent p-5" data-testid="daily-matches-active-banner">
    <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-cyan-500/20 blur-3xl pointer-events-none" />
    <div className="relative flex items-start gap-4">
      <div className="w-11 h-11 rounded-xl bg-cyan-500/20 text-cyan-300 flex items-center justify-center shrink-0">
        <Bell className="w-5 h-5" />
      </div>
      <div className="flex-1">
        <p className="text-[10px] font-black uppercase tracking-widest text-cyan-300 mb-1">Daily Matches · Active</p>
        <h3 className="text-base font-black text-white mb-1">
          Day {daysActive} · {totalRvs} RVs delivered so far
        </h3>
        <p className="text-sm text-slate-300">
          Every morning we scan fresh listings and drop 3 hand-picked matches here based on your match profile. You keep this feed for life — no subscription.
        </p>
      </div>
    </div>
  </div>
);

const FeedDay = ({ feed, email }) => {
  const label = useMemo(() => {
    if (!feed?.feed_date) return '';
    const d = new Date(`${feed.feed_date}T00:00:00Z`);
    return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  }, [feed]);
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 sm:p-5" data-testid={`daily-feed-${feed.feed_date}`}>
      <div className="flex items-center gap-2 mb-3">
        <Sparkles className="w-3.5 h-3.5 text-amber-300" />
        <p className="text-[11px] font-black uppercase tracking-widest text-amber-300">{label}</p>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {(feed.rvs || []).map((rv, i) => (
          <RVCard key={rv.feed_match_id || i} rv={rv} feedId={feed.id} email={email} />
        ))}
      </div>
    </div>
  );
};

const RVCard = ({ rv, feedId, email }) => {
  const cardRef = useRef(null);
  const seenRef = useRef(false);

  const track = (action) => {
    axios.post(`${API}/daily-matches/track`, {
      email, feed_id: feedId, rv_id: rv.feed_match_id, action,
    }).catch(() => { /* fail silent — tracking is non-critical */ });
  };

  // Fire a single 'view' event when the card first scrolls into the viewport.
  useEffect(() => {
    if (!cardRef.current || seenRef.current) return;
    const node = cardRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting && !seenRef.current) {
            seenRef.current = true;
            track('view');
            observer.disconnect();
          }
        });
      },
      { threshold: 0.4 },
    );
    observer.observe(node);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const title = [rv.year, rv.brand, rv.model].filter(Boolean).join(' ') || 'RV';
  const price = rv.price ? `$${Number(rv.price).toLocaleString('en-US')}` : (rv.msrp_range || '—');
  const searchHref = rv.google_search_link || `https://duckduckgo.com/?q=${encodeURIComponent(title)}+for+sale`;

  return (
    <article
      ref={cardRef}
      className="rounded-xl border border-white/10 bg-black/40 overflow-hidden flex flex-col hover:border-cyan-400/40 transition-colors"
      data-testid={`daily-rv-${rv.feed_match_id}`}
    >
      {rv.image_url && (
        <button
          type="button"
          onClick={() => track('click')}
          className="block aspect-video w-full bg-black/50 overflow-hidden"
        >
          <img src={rv.image_url} alt={title} className="w-full h-full object-cover" loading="lazy" />
        </button>
      )}
      <div className="p-3 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-2 mb-1.5">
          <h4 className="text-sm font-black text-white leading-tight">{title}</h4>
          {typeof rv.score === 'number' && (
            <span className="shrink-0 text-[10px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300 border border-emerald-400/30">
              {rv.score}%
            </span>
          )}
        </div>
        <div className="flex items-center gap-3 text-[11px] text-slate-400 mb-2 flex-wrap">
          <span className="inline-flex items-center gap-1"><DollarSign size={11} />{price}</span>
          {rv.sleeps ? <span className="inline-flex items-center gap-1"><Users size={11} />sleeps {rv.sleeps}</span> : null}
          {rv.length ? <span className="inline-flex items-center gap-1"><Ruler size={11} />{rv.length} ft</span> : null}
        </div>
        {(rv.reasons || []).slice(0, 2).map((r, i) => (
          <p key={i} className="text-[11px] text-slate-300 leading-snug mb-0.5">
            <TrendingUp className="inline w-3 h-3 text-emerald-300 mr-1" />{r}
          </p>
        ))}
        <div className="mt-auto pt-3 flex items-center gap-2">
          <a
            href={searchHref}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => track('outbound')}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full bg-cyan-400 hover:bg-cyan-300 text-[#05070d] text-[11px] font-black"
            data-testid={`daily-rv-view-${rv.feed_match_id}`}
          >
            View listings <ExternalLink className="w-3 h-3" />
          </a>
          <button
            type="button"
            onClick={() => track('click')}
            className="inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full border border-white/15 text-slate-200 text-[11px] font-bold hover:bg-white/[0.05]"
          >
            Save <ChevronRight className="w-3 h-3" />
          </button>
        </div>
      </div>
    </article>
  );
};

const DailyMatchesPaywall = () => (
  <div className="relative overflow-hidden rounded-2xl border-2 border-amber-400/40 bg-gradient-to-br from-amber-500/15 via-orange-500/10 to-transparent p-6 sm:p-8" data-testid="daily-matches-paywall">
    <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full bg-amber-400/20 blur-3xl pointer-events-none" />
    <div className="relative">
      <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-widest px-2 py-1 rounded-full bg-amber-400 text-[#05070d] mb-3">
        <Bell size={10} /> Daily Matches
      </span>
      <h3 className="text-2xl sm:text-3xl font-black text-white mb-2">
        3 fresh RVs, hand-picked for you. Every day. Forever.
      </h3>
      <p className="text-sm sm:text-base text-slate-200 mb-4 max-w-2xl">
        Fresh personalized RV matches every day based on your profile, budget, and tow rig. New listings, price drops, and units you may have missed — all delivered right to your dashboard. One-time payment, lifetime access.
      </p>
      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-sm text-slate-200 mb-5 max-w-2xl">
        <li className="flex items-start gap-2"><Search className="w-4 h-4 text-emerald-300 shrink-0 mt-0.5" />3 personalized picks / day</li>
        <li className="flex items-start gap-2"><Search className="w-4 h-4 text-emerald-300 shrink-0 mt-0.5" />Direct link to real listings</li>
        <li className="flex items-start gap-2"><Search className="w-4 h-4 text-emerald-300 shrink-0 mt-0.5" />Uses your match profile</li>
        <li className="flex items-start gap-2"><Search className="w-4 h-4 text-emerald-300 shrink-0 mt-0.5" />One-time $19.99 · Lifetime feed</li>
      </ul>
      <div className="flex items-center gap-3 flex-wrap">
        <Link
          to="/checkout/daily_matches"
          className="inline-flex items-center gap-1.5 px-5 py-3 rounded-2xl bg-amber-400 hover:bg-amber-300 text-[#05070d] font-black text-sm shadow-[0_10px_40px_-10px_rgba(245,158,11,0.6)]"
          data-testid="daily-matches-checkout-cta"
        >
          Turn on Daily Matches — $19.99
        </Link>
        <span className="inline-flex items-center gap-1 text-[11px] text-slate-400">
          <Lock className="w-3 h-3" /> One-time payment. No subscription.
        </span>
      </div>
    </div>
  </div>
);

export default DailyMatchesFeed;
